import { Box, Center, Img, Stack, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { faFile } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import Dropzone from 'react-dropzone'

import {
  DEFAULT_ACCEPETED_FILE_TYPES,
  FILE_TYPE_TO_EXTENSION,
  FILE_TYPE_TO_ICON,
} from './constants'
import type { FileType } from './types'

const MEGABYTE_TO_BYTES = 1000 * 1000

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}

export default function FileUploadDropzone({
  maxFiles,
  maxSizeMegabytes = 10,
  onChange,
  initialFiles = [],
  acceptedFileTypes = DEFAULT_ACCEPETED_FILE_TYPES,
}: {
  maxFiles: number
  maxSizeMegabytes: number
  onChange: (files: File[]) => void
  initialFiles?: File[]
  acceptedFileTypes?: FileType[]
}): React.ReactElement {
  const [files, setFiles] = React.useState<File[]>(initialFiles)
  return (
    <Box>
      <Dropzone
        onDrop={newFiles => {
          setFiles(newFiles)
          onChange(newFiles)
        }}
        accept={acceptedFileTypes}
        maxSize={maxSizeMegabytes * MEGABYTE_TO_BYTES}
        maxFiles={maxFiles}
        multiple={maxFiles > 1}
      >
        {({ getRootProps, getInputProps }) => (
          <Box
            borderRadius={4}
            border="2px solid"
            borderColor="primary.border"
            borderStyle="dashed"
            textAlign="center"
            cursor="pointer"
          >
            <Box {...getRootProps()} p={6}>
              <input {...getInputProps()} />
              <Stack direction="column" spacing={2}>
                {maxFiles === 1 ? (
                  <Text color="fg.subtle" fontSize="20px" fontWeight={500}>
                    Drag and drop a file here, or click to select a file (max {maxSizeMegabytes}{' '}
                    MB).
                  </Text>
                ) : (
                  <>
                    <Text color="fg.subtle" fontSize="20px" fontWeight={500}>
                      Drag and drop some files here, or click to select files. You may select at
                      most {maxFiles} files (at most {maxSizeMegabytes} MB).
                    </Text>
                    <Text color="fg.subtle">
                      Note: If you&apos;re submitting multiple files, you must select them at the
                      same time!
                    </Text>
                  </>
                )}
                <Text color="fg.subtle">
                  (Accepted file types:{' '}
                  {acceptedFileTypes.map(t => FILE_TYPE_TO_EXTENSION[t]).join(', ')})
                </Text>
              </Stack>
            </Box>
          </Box>
        )}
      </Dropzone>
      {files.length === 0 ? null : (
        <Center>
          <Wrap mt={4} spacing="16px">
            {files.map(file => (
              <WrapItem key={`${file.name}-${file.lastModified}`}>
                <Center
                  border="2px solid"
                  borderColor="border"
                  p={3}
                  borderRadius="8px"
                  height="200px"
                >
                  {isImageFile(file) ? (
                    <Img maxWidth="100%" maxHeight="100%" src={URL.createObjectURL(file)} />
                  ) : (
                    <Stack direction="column" spacing={3} align="center" textAlign="center">
                      <Text fontSize="48px" color="gray.emphasized">
                        <FontAwesomeIcon icon={FILE_TYPE_TO_ICON[file.type] ?? faFile} />
                      </Text>
                      <Text maxWidth="200px" color="fg.muted">
                        {file.name}
                      </Text>
                    </Stack>
                  )}
                </Center>
              </WrapItem>
            ))}
          </Wrap>
        </Center>
      )}
    </Box>
  )
}
